"use strict";

import { Application } from "../../main/application.js";
import { Mouse } from "../../systems/core/mouse.js";
import { EventListener } from "../../systems/secondary/eventListener.js";
import { UIContainer } from "../uiContainer.js";
import { UISprite } from "../uiSprite.js";
import { UIButton } from "./uiButton.js";
import { UIDragButton } from "./uiButton.js";
import { UISlider } from "./uiSlider.js";

export class UIScrollArea extends UIContainer {
    constructor(config) {
        let _onInitialize = config.onInitialize;
        config.onInitialize = undefined;

        super(config);

        this.touchScrollSpeed = config.touchScrollSpeed ?? 3;
        this.mouseScrollSpeed = config.mouseScrollSpeed ?? 1;
        this.accelerationThreshold = config.accelerationThreshold ?? 10;
        this.maxScrollOffset = config.maxScrollOffset ?? 1;

        this.alwaysViewScrollbar = config.alwaysViewScrollbar ?? false;
        this.keepScrollbarHidden = config.keepScrollbarHidden ?? this.alwaysViewScrollbar;
        this.showScrollbar = this.alwaysViewScrollbar || !this.keepScrollbarHidden;
        this.useDragging = config.useDragging ?? !Application.main.device.isDesktop;
        this.lockScroll = false;

        this._focusedScroll = Application.main.device.isDesktop ?
            !(config.focusedScroll ?? false) : false;
        this.isFocused = false;
        if (Application.main.device.isDesktop) {
            this.focusedScroll = !this.focusedScroll;
            this._focusChecker = {
                position: { x: 0, y: 0 },
                size: { width: 0, height: 0 }
            };
            Application.main.ticker.add(this.scrollBoundsCheck.bind(this));
        }
        
        this.contentY = 0;
        this.onScroll = config.onScroll;

        this.interactiveArea = new UIDragButton({
            graphics: new UISprite({
                rect: {
                    anchorX: { min: 0, max: 1 },
                    anchorY: { min: 0, max: 1 }
                },
                onInitialize: function() {
                    if (config.debug) {
                        this.alpha = 0.5;
                    }
                }
            }),
            rect: {...config.areaRect},
            parent: this,
            onDrag: (id,pointer) => {
                if (!this.useDragging || !this.showScrollbar) return;
                this.scroll(-(pointer.y - this.interactiveArea._prevY) * this.touchScrollSpeed);
            }
        });

        this.contentBox = new UIContainer({
            rect: {...config.areaRect},
            parent: this
        });
        this.contentContainer = new UIContainer({
            rect: {
                anchorX: { min: 0, max: 1 },
                anchorY: { min: 1, max: 1 },
                pivot: { x: 0, y: 1 }
            },
            parent: this.contentBox
        });
        this.contents = {};
        this._contentCount = 0;

        if (!config.debug) {
            this.contentBox.mask = this.interactiveArea.graphics;
        }

        if (this.useDragging) {
            this.frontInteractiveArea = new UIDragButton({
                graphics: new UISprite({
                    rect: {
                        anchorX: { min: 0, max: 1 },
                        anchorY: { min: 0, max: 1 }
                    },
                    onInitialize: function() {
                        this.alpha = 0.5;
                    }
                }),
                rect: {...config.areaRect},
                parent: this,
                onDrag: (id,pointer) => {
                    if (!this.showScrollbar && !this.keepScrollbarHidden) return;
                    this.scroll(-(pointer.y - this.frontInteractiveArea._prevY) * this.touchScrollSpeed);
                },
                onDragEnded: () => this.frontInteractiveArea.hide(),
                visible: false,
                renderable: false
            });

            this.takeControl = (pointer,delta) => {
                this.frontInteractiveArea.show(true);

                if (delta !== undefined) {
                    this.frontInteractiveArea.setDelta(delta);
                }
        
                this.frontInteractiveArea._onPointerDown(pointer);
            };
            this.releaseControl = () => {
                if (!this.frontInteractiveArea._isDown) return;
                this.frontInteractiveArea._onPointerUp();
            }
        }

        // Visual objects
        let scrollbarConfig = config.scrollbarConfig;
        if (scrollbarConfig === undefined) {
            scrollbarConfig = {
                rect: { },
                bar: { },
                indicator: { }
            }
        }
        
        this.scrollbar = new UISlider({
            rect: scrollbarConfig.rect,
            parent: this,
            bar: new UIButton({
                textureID: scrollbarConfig.bar.textureID,
                cornerSize: scrollbarConfig.bar.cornerSize,
                rect: {
                    size: scrollbarConfig.bar.size
                },
                buttonMode: false
            }),
            from: new UISprite({
                visible: false
            }),
            to: new UISprite({
                visible: false
            }),
            indicator: new UIDragButton({
                textureID: scrollbarConfig.indicator.textureID,
                cornerSize: scrollbarConfig.indicator.cornerSize,
                rect: {
                    size: scrollbarConfig.indicator.size
                }
            }),
            orientation: "vertical",
            useIndicatorOffset: true,
            onSlide: (i,pos,delta) => this.slide(delta)
        });

        Mouse.registerMouseScroll(data => {
            if (!this.worldVisible || !this.showScrollbar) return;
            this.scroll(data.deltaY * this.mouseScrollSpeed);
        });

        this.updateNow = () => {
            super.updateNow();
            this.updateSettings();
        }

        _onInitialize?.bind(this)(config);
        this.updateNow();

        this.ext_onResolutionChanged = this.onResolutionChanged;
        this.onResolutionChanged = () => {
            this.ext_onResolutionChanged();
            this._refreshIndicatorSize();
            this.scrollbar.onResolutionChanged();
        }
    }

    get focusedScroll() {
        return this._focusedScroll;
    }
    set focusedScroll(value) {
        if (this._focusedScroll == value || !Application.main.device.isDesktop) return;
        this._focusedScroll = value;
    }

    set lockScroll(value) {
        this._lockScroll = value;
    }

    get contentHeight() {
        return this.contentBox.getLocalBounds().height;
    }

    get contentCount() {
        return this._contentCount;
    }

    scrollBoundsCheck() {
        if (!this._focusedScroll || !this.worldVisible || (!this.showScrollbar && !this.keepScrollbarHidden)) return;

        this._focusChecker.position = this.getRealWorldPosition();
        this._focusChecker.size.width = this._width;
        this._focusChecker.size.height = this._height;

        this.isFocused =
            Application.main.mouse.global.x >= this._focusChecker.position.x -
                this._focusChecker.size.width / 2 &&
            Application.main.mouse.global.y >= this._focusChecker.position.y -
                this._focusChecker.size.height / 2 &&
            Application.main.mouse.global.x <= this._focusChecker.position.x +
                this._focusChecker.size.width / 2 &&
            Application.main.mouse.global.y <= this._focusChecker.position.y +
                this._focusChecker.size.height / 2;
    }

    // Contents
    addObject(object,id,anchorTo) {
        if (id === undefined) {
            this.contentCount;
        }

        this.contents[id] = object;
        this._contentCount = Object.keys(this.contents).length;

        if (object.setParent !== undefined) {
            object.setParent(this.contentContainer);
        }

        if (object.setMaxWidth !== undefined) {
            let areaWidth = this.interactiveArea.calculatedRect.size.width -
                (object.rect.position.x + object.rect.marginX.left + object.rect.marginX.right);
            object.setMaxWidth(areaWidth);
        }

        if (anchorTo !== undefined && typeof anchorTo.object === "string") {
            let to = anchorTo.object;
            let axis = anchorTo.axis ?? "y";

            let offset = anchorTo.offset ?? { x: 0, y: 0 };
            if (typeof offset === "number") {
                if (axis == "x") {
                    offset = { x: offset, y: 0 };
                } else if (axis == "y") {
                    offset = { x: 0, y: offset };
                } else {
                    offset = { x: offset, y: offset };
                }
            }

            if (this.contents[to]._onResolutionChangedCallback === undefined) {
                this.contents[to]._onResolutionChangedListener = new SimpleEngine.EventListener();
                this.contents[to]._onResolutionChangedCallback = function() {
                    this._onResolutionChangedListener.dispatch();
                };
            }
    
            this.contents[to]._onResolutionChangedListener.registerCallback(() => {
                if (axis.includes("y")) {
                    object.rect.position.y = this.contents[to].rect.position.y +
                        -(this.contents[to].heightUI + offset.y);
                }
                if (axis.includes("x")) {
                    object.rect.position.x = this.contents[to].rect.position.x + offset.x;
                }
            });
        }

        if (this.useDragging && object instanceof UIButton) {
            if (object.onPointerMove === undefined) {
                object.onPointerMove = (id,pointer) => this.takeControl(pointer);
            }

            if (object.onPointerUpOutside === undefined) {
                object.onPointerUpOutside = () => this.releaseControl();
            }
        }

        this.updateNow();
    }
    find(key) {
        if (key === undefined) return;
        return this.contents[key];
    }
    clear(dispose) {
        if (this.contentCount > 0) {
            if (dispose) {
                Object.values(this.contents).splice(0, this.contentCount).forEach(e => e?.dispose());
            }
            this.contents = {};
            this._contentCount = 0;
        }

        while (this.contentContainer.children[0]) {
            this.contentContainer.removeChild(this.contentContainer.children[0]);
        }

        this.updateSettings();
    }

    reset() {
        this.contentY = 0;
        this.scrollbar.setDelta(0);
    }

    updateSettings() {
        this._updateScrollbarVisibility();
    }

    _updateScrollbarVisibility() {
        this.showScrollbar = this.alwaysViewScrollbar;
        
        if (!this.alwaysViewScrollbar && !this.keepScrollbarHidden) {
            this.showScrollbar = this.contentContainer.height > 
                this.transformToUI(this.interactiveArea.calculatedRect.size.height);
        }
        
        if (this.showScrollbar) {
            this.scrollbar.show(true);
        } else {
            this.scrollbar.hide();
        }

        this._refreshIndicatorSize();
    }
    _refreshIndicatorSize() {
        let scrollbarHeight = this.transformToUI(this.scrollbar.calculatedRect.size.height);
        
        let delta = (scrollbarHeight / this.contentHeight);
        if (delta === Infinity || delta < 0.25) {
            delta = 0.25;
        }

        this.scrollbar.indicator.rect.size.height = this.transformFromUI(scrollbarHeight * delta);
        this.scrollbar.indicator.updateNow();

        this.scrollbar.calculateIndicatorOffset();
    }

    scroll(direction) {
        if (this._lockScroll || (this.focusedScroll && !this.isFocused)) return;

        let maxBottom = Math.max(this.transformFromUI(
            this.contentBox.getLocalBounds().height -
            this.contentBox._height * this.maxScrollOffset), 0);
            
        let acceleration = Math.min(1, Math.abs(direction) / this.accelerationThreshold);
        this.contentY = Math.clamp(this.contentY + direction * acceleration, 0, maxBottom);

        this.contentContainer.rect.position.y = this.contentY;
        this.contentContainer.updateNow();

        this.scrollbar.setDelta(this.contentY / maxBottom, true);
        this.onScroll?.(this.contentY, this.scrollbar.delta);
    }

    slide(delta) {
        if (this._lockScroll) return;

        let maxBottom = Math.max(this.transformFromUI(
            this.contentBox.getLocalBounds().height -
            this.contentBox._height * this.maxScrollOffset), 0);

        this.contentY = delta * maxBottom;

        if (this.showScrollbar) {
            this.scrollbar.show();
            this.contentContainer.rect.position.y = this.contentY;
            this.contentContainer.updateNow();
            this.onScroll?.(this.contentY, this.scrollbar.delta);
        } else {
            this.scrollbar.hide();
        }
    }

    onOrientationChanged() {
        super.onOrientationChanged();

        // Updates the max width of the texts
        let contentValues = Object.values(this.contents);
        for (let i = 0; i < contentValues.length; i++) {
            let object = contentValues[i];
            if (object.setMaxWidth === undefined) continue;

            let areaWidth = this.interactiveArea.calculatedRect.size.width -
                (object.rect.position.x + object.rect.marginX.left + object.rect.marginX.right);
            object.setMaxWidth(areaWidth);
        }

        // Updates the ContentContainer size
        let maxBottom = Math.max(this.transformFromUI(
            this.contentBox.getLocalBounds().height -
            this.contentBox._height * this.maxScrollOffset), 0);
            
        this.contentY = Math.clamp(this.contentY, 0, maxBottom);

        this.contentContainer.rect.position.y = this.contentY;
        this.contentContainer.updateNow();

        this.scrollbar.setDelta(this.contentY / maxBottom, true);

        this.updateSettings();
    }
}